import { queryKeys } from '@query/keys'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { useIBRoutesConditions } from '@pages/IntroducingBroker/IntroducingBrokerRoutes'

import { useAccountReadContext } from '@context/AccountContextContext'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { AccountTypeArr, CommissionPlan, UpdateCommissionPlan } from '@model/MultiTierDto'

export interface CommissionPlanData {
  commissionPlans: CommissionPlan[]
  id: string
  name: string
  isDefault: boolean
}

interface CommissionPlans {
  commissionPlans: CommissionPlanData[]
  forceRefresh: () => void
  isLoading: boolean
  handleCommissionPlan: (
    commissionPlans: UpdateCommissionPlan[],
    commissionPlanId: string
  ) => Promise<void>
}

export const useCommissionPlans = (): CommissionPlans => {
  const apiClient = useApiClient(ClientApiClient)
  const { account } = useAccountReadContext()
  const { isLastMultiTierLevel } = useIBRoutesConditions()
  const queryClient = useQueryClient()

  const introducingBrokerId = account?.clientIntroducingBroker?.id
  const queryKey = queryKeys.ib.commissionPlans(introducingBrokerId)

  const { data: commissionPlans, isLoading } = useQuery({
    queryKey,
    queryFn: async () => {
      const data = await apiClient.getIBCommissionPlans(introducingBrokerId!)

      return data.map((item) => ({
        ...item,
        commissionPlans: item.commissionPlans.sort((a, b) => {
          return (
            AccountTypeArr.indexOf(a.tradingAccountType.name) -
            AccountTypeArr.indexOf(b.tradingAccountType.name)
          )
        }),
      }))
    },
    enabled: !!introducingBrokerId && isLastMultiTierLevel === false,
  })

  const forceRefresh = () => queryClient.invalidateQueries({ queryKey })

  const handleCommissionPlan = async (
    commissionPlans: UpdateCommissionPlan[],
    commissionPlanId: string
  ) => {
    const ibId = account?.clientIntroducingBroker?.id || ''
    if (commissionPlanId) {
      await apiClient.updateCommissionPlan(ibId, commissionPlanId, commissionPlans)
    } else {
      await apiClient.addCommissionPlan(ibId, commissionPlans)
    }
    forceRefresh()
  }

  return { commissionPlans: commissionPlans ?? [], forceRefresh, handleCommissionPlan, isLoading }
}
